/**
 * HTTP Wrapper Module
 *
 * Provides fetch wrapper functions with:
 * - Static CSRF header injection for BFF endpoints (/oauth-agent/*)
 * - Consistent response handling (JSON parsing, error throwing)
 * - Single-flight token refresh coordination for 401 responses from /api/*
 *
 * @module api/http
 */

import { createApiError, DEFAULT_ERROR_MESSAGES } from '../types/errors';
import { getApiBaseUrl } from '../config';

// =============================================================================
// Constants
// =============================================================================

/**
 * Header name required by the BFF for CSRF protection.
 * See: oidc-bff/internal/agent/middleware.go (staticHeaderMiddleware)
 */
export const BFF_HEADER_NAME = 'X-MP-Token-Handler';

/**
 * Header value required by the BFF for CSRF protection.
 */
export const BFF_HEADER_VALUE = '1';

/**
 * Path prefixes that require the static CSRF header.
 *
 * Both the oidc-bff agent (/oauth-agent/*) and the Envoy ext_proc OAuth Proxy
 * (/api/*) validate this header on every non-preflight request. The proxy
 * enforces it as step 0 — before the cookie/JWT check — so any /api/* call
 * without the header is rejected with 401 before authentication even runs.
 * (Source: oidc-bff/internal/proxy/proxy.go, handleRequestHeaders step 0.)
 */
const BFF_PATH_PREFIXES = ['/oauth-agent/', '/api/'] as const;

/**
 * Path prefix for API endpoints that should trigger auto-refresh on 401.
 */
const API_PATH_PREFIX = '/api/';

/**
 * Refresh endpoint path.
 */
const REFRESH_PATH = '/oauth-agent/refresh';

/**
 * Maximum number of refresh retries on transient failures (e.g., 502).
 */
const MAX_REFRESH_RETRIES = 3;

/**
 * Initial backoff delay in milliseconds for refresh retries.
 */
const INITIAL_BACKOFF_MS = 500;

// =============================================================================
// Base URL Resolution
// =============================================================================

/**
 * Resolves a request path to a full URL by prepending the configured API
 * base URL (see `getApiBaseUrl` in the config module).
 *
 * Request paths always start with `/`, so concatenation with the
 * trailing-slash-stripped base produces a well-formed URL.
 */
function resolveUrl(path: string): string {
  return getApiBaseUrl() + path;
}

// =============================================================================
// Error Types
// =============================================================================

/**
 * Error thrown when the session has permanently expired.
 *
 * This occurs when a token refresh fails with 401, indicating the refresh token
 * is invalid or expired. Callers should handle this by clearing auth state
 * and redirecting to login.
 */
export class SessionExpiredError extends Error {
  public readonly name = 'SessionExpiredError';

  constructor(message = 'Session expired. Please sign in again.') {
    super(message);
    // Maintain proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, SessionExpiredError.prototype);
  }
}

/**
 * Type guard to check if an error is a SessionExpiredError.
 */
export function isSessionExpiredError(error: unknown): error is SessionExpiredError {
  return error instanceof SessionExpiredError;
}

/**
 * Error thrown for HTTP errors with additional context.
 */
export class HttpError extends Error {
  public readonly name = 'HttpError';
  public readonly status: number;
  public readonly statusText: string;
  public readonly body?: unknown;

  constructor(status: number, statusText: string, body?: unknown) {
    const message = typeof body === 'object' && body !== null && 'message' in body
      ? String((body as { message: unknown }).message)
      : DEFAULT_ERROR_MESSAGES[status] ?? `HTTP ${status}: ${statusText}`;

    super(message);
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    Object.setPrototypeOf(this, HttpError.prototype);
  }

  /**
   * Convert to an ApiError for use with error type guards.
   */
  toApiError() {
    return createApiError(
      this.status,
      this.message,
      this.body && typeof this.body === 'object' && 'code' in this.body
        ? (this.body as { code: string }).code as import('../types/errors').ApiErrorCode
        : undefined,
      this.body && typeof this.body === 'object' && 'details' in this.body
        ? (this.body as { details: Record<string, unknown> }).details
        : undefined
    );
  }
}

/**
 * Type guard to check if an error is an HttpError.
 */
export function isHttpError(error: unknown): error is HttpError {
  return error instanceof HttpError;
}

// =============================================================================
// Single-Flight Refresh Coordination
// =============================================================================

/**
 * Module-level promise tracking for single-flight refresh.
 * When a refresh is in progress, this holds the promise so concurrent
 * 401 handlers can wait on the same refresh request.
 */
let refreshPromise: Promise<boolean> | null = null;

/**
 * Get the current refresh promise (for testing).
 * @internal
 */
export function _getRefreshPromise(): Promise<boolean> | null {
  return refreshPromise;
}

/**
 * Reset the refresh state (for testing).
 * @internal
 */
export function _resetRefreshState(): void {
  refreshPromise = null;
}

/**
 * Performs a token refresh with retry logic for transient failures.
 *
 * @param fetchFn - The fetch function to use (allows injection for testing)
 * @returns true if refresh succeeded, throws SessionExpiredError if permanently failed
 */
async function performRefreshWithRetry(
  fetchFn: typeof fetch = fetch
): Promise<boolean> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < MAX_REFRESH_RETRIES; attempt++) {
    try {
      const response = await fetchFn(resolveUrl(REFRESH_PATH), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          [BFF_HEADER_NAME]: BFF_HEADER_VALUE,
        },
      });

      if (response.ok) {
        return true;
      }

      // 401 means session is permanently dead
      if (response.status === 401) {
        throw new SessionExpiredError();
      }

      // 502/503/504 are transient - retry with backoff
      if (response.status >= 502 && response.status <= 504) {
        lastError = new HttpError(response.status, response.statusText);

        if (attempt < MAX_REFRESH_RETRIES - 1) {
          const backoff = INITIAL_BACKOFF_MS * Math.pow(2, attempt);
          await sleep(backoff);
          continue;
        }
      }

      // Other errors - don't retry
      const body = await parseResponseBody(response);
      throw new HttpError(response.status, response.statusText, body);
    } catch (error) {
      // Re-throw SessionExpiredError immediately
      if (isSessionExpiredError(error)) {
        throw error;
      }

      // For network errors or other exceptions, retry
      if (error instanceof Error) {
        lastError = error;
      }

      if (attempt >= MAX_REFRESH_RETRIES - 1) {
        throw lastError ?? new Error('Refresh failed after retries');
      }

      const backoff = INITIAL_BACKOFF_MS * Math.pow(2, attempt);
      await sleep(backoff);
    }
  }

  throw lastError ?? new Error('Refresh failed');
}

/**
 * Attempts to refresh the access token using single-flight coordination.
 *
 * If a refresh is already in progress, waits for the existing refresh.
 * Otherwise, initiates a new refresh request.
 *
 * @param fetchFn - The fetch function to use (allows injection for testing)
 * @returns true if refresh succeeded
 * @throws SessionExpiredError if the session is permanently expired
 */
async function refreshAccessToken(fetchFn: typeof fetch = fetch): Promise<boolean> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start a new refresh and store the promise
  refreshPromise = performRefreshWithRetry(fetchFn).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

// =============================================================================
// Response Handling
// =============================================================================

/**
 * Parses the response body as JSON if possible.
 */
async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Handles response parsing and error throwing.
 *
 * - 2xx responses: Parse and return JSON
 * - 204 No Content: Return undefined
 * - Non-2xx responses: Throw HttpError with status and parsed body
 */
async function handleResponse<T>(response: Response): Promise<T> {
  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  // Success responses
  if (response.ok) {
    return response.json() as Promise<T>;
  }

  // Error responses - parse body and throw
  const body = await parseResponseBody(response);
  throw new HttpError(response.status, response.statusText, body);
}

// =============================================================================
// Header Injection
// =============================================================================

/**
 * Checks if a path requires the static CSRF header.
 *
 * Both /oauth-agent/* (oidc-bff agent) and /api/* (Envoy ext_proc OAuth Proxy)
 * validate X-MP-Token-Handler on every non-preflight request.
 */
function requiresBffHeader(path: string): boolean {
  return BFF_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Checks if a path should trigger auto-refresh on 401.
 */
function shouldAutoRefresh(path: string): boolean {
  return path.startsWith(API_PATH_PREFIX);
}

/**
 * Builds headers for a request, adding the BFF header if needed.
 */
function buildHeaders(path: string): Headers {
  const headers = new Headers();

  // Add static CSRF header for /oauth-agent/* and /api/* paths.
  if (requiresBffHeader(path)) {
    headers.set(BFF_HEADER_NAME, BFF_HEADER_VALUE);
  }

  return headers;
}

// =============================================================================
// HTTP Methods
// =============================================================================

/**
 * Options for HTTP requests.
 */
export interface HttpOptions {
  /**
   * Custom fetch function for testing.
   * @default globalThis.fetch
   */
  fetch?: typeof fetch;
}

/**
 * Utility function to sleep for a given duration.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Performs an HTTP request with auto-refresh on 401.
 */
async function httpRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  options?: HttpOptions
): Promise<T> {
  const fetchFn = options?.fetch ?? fetch;

  const makeRequest = async (): Promise<Response> => {
    const headers = buildHeaders(path);

    if (body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }

    const init: RequestInit = {
      method,
      credentials: 'include',
      headers,
      ...(body !== undefined && { body: JSON.stringify(body) }),
    };

    return fetchFn(resolveUrl(path), init);
  };

  let response = await makeRequest();

  // Handle 401 from /api/* paths with auto-refresh
  if (response.status === 401 && shouldAutoRefresh(path)) {
    // This will throw SessionExpiredError if refresh fails with 401,
    // or HttpError for other refresh failures
    await refreshAccessToken(fetchFn);
    // Retry the original request after successful refresh
    response = await makeRequest();
  }

  return handleResponse<T>(response);
}

/**
 * Performs an HTTP GET request.
 *
 * Features:
 * - Automatically adds X-MP-Token-Handler header for /oauth-agent/* paths
 * - Automatically handles 401 from /api/* with token refresh and retry
 * - Parses JSON responses, handles 204 No Content
 * - Throws HttpError for non-2xx responses
 *
 * @param path - The request path (e.g., '/api/users', '/oauth-agent/claims')
 * @param options - Optional configuration including custom fetch
 * @returns Parsed JSON response
 * @throws HttpError for non-2xx responses
 * @throws SessionExpiredError if refresh fails with 401
 *
 * @example
 * // Simple GET
 * const claims = await httpGet<Claims>('/oauth-agent/claims');
 *
 * @example
 * // With mocked fetch for testing
 * const users = await httpGet<User[]>('/api/users', { fetch: mockFetch });
 */
export async function httpGet<T>(path: string, options?: HttpOptions): Promise<T> {
  return httpRequest<T>('GET', path, undefined, options);
}

/**
 * Performs an HTTP POST request with JSON body.
 *
 * Features:
 * - Automatically adds X-MP-Token-Handler header for /oauth-agent/* paths
 * - Automatically adds Content-Type: application/json when body is provided
 * - Automatically handles 401 from /api/* with token refresh and retry
 * - Parses JSON responses, handles 204 No Content
 * - Throws HttpError for non-2xx responses
 *
 * @param path - The request path (e.g., '/api/users/invite', '/oauth-agent/refresh')
 * @param body - Optional JSON body to send
 * @param options - Optional configuration including custom fetch
 * @returns Parsed JSON response
 * @throws HttpError for non-2xx responses
 * @throws SessionExpiredError if refresh fails with 401
 *
 * @example
 * // POST with body
 * const response = await httpPost<InviteResponse>('/api/users/invite', { email: 'user@example.com' });
 *
 * @example
 * // POST without body
 * const result = await httpPost<RefreshResponse>('/oauth-agent/refresh');
 */
export async function httpPost<T>(
  path: string,
  body?: unknown,
  options?: HttpOptions
): Promise<T> {
  return httpRequest<T>('POST', path, body, options);
}

/**
 * Performs an HTTP PUT request with JSON body.
 *
 * Same behavior as {@link httpPost} (CSRF header, Content-Type when a body is
 * provided, 401 auto-refresh, JSON/204 handling) with the PUT verb — used for
 * full replacement of a sub-resource (e.g. a member's role).
 *
 * @example
 * const res = await httpPut<UpdateUserRoleResponse>(
 *   '/api/tenant-management/orgs/o1/users/u1/role',
 *   { role: 'admin' },
 * );
 */
export async function httpPut<T>(
  path: string,
  body?: unknown,
  options?: HttpOptions
): Promise<T> {
  return httpRequest<T>('PUT', path, body, options);
}

/**
 * Performs an HTTP PATCH request with JSON body.
 *
 * Same behavior as {@link httpPost} (CSRF header, Content-Type when a body is
 * provided, 401 auto-refresh, JSON/204 handling) with the PATCH verb — used
 * for partial updates where only the provided fields change (e.g. tenant
 * details).
 *
 * @example
 * const res = await httpPatch<UpdateTenantResponse>(
 *   '/api/tenant-management/orgs/o1',
 *   { subscriptionStatus: 'active' },
 * );
 */
export async function httpPatch<T>(
  path: string,
  body?: unknown,
  options?: HttpOptions
): Promise<T> {
  return httpRequest<T>('PATCH', path, body, options);
}

/**
 * Performs an HTTP DELETE request.
 *
 * Features:
 * - Automatically adds X-MP-Token-Handler header for /oauth-agent/* and /api/* paths
 * - Automatically handles 401 from /api/* with token refresh and retry
 * - Parses JSON responses, handles 204 No Content (returns undefined)
 * - Throws HttpError for non-2xx responses
 *
 * DELETE requests carry no body. A 204 No Content response (the common success
 * shape for a delete) resolves to `undefined`; a 2xx response with a JSON body
 * is parsed and returned.
 *
 * @param path - The request path (e.g., '/api/tenant-management/orgs/o1/users/u1')
 * @param options - Optional configuration including custom fetch
 * @returns Parsed JSON response, or undefined for 204 No Content
 * @throws HttpError for non-2xx responses
 * @throws SessionExpiredError if refresh fails with 401
 *
 * @example
 * // DELETE a member; backend returns 204 No Content
 * await httpDelete<void>('/api/tenant-management/orgs/o1/users/u1');
 */
export async function httpDelete<T>(
  path: string,
  options?: HttpOptions
): Promise<T> {
  return httpRequest<T>('DELETE', path, undefined, options);
}
